<template>
  <div :class="['body', styles]">
    <h2 :style="{fontSize: fontSize+3 + 'px', textAlign: 'center'}">{{ titleText }}</h2>
    <p v-if="authorText" class="story-author" :style="{fontSize: fontSize-2 + 'px', textAlign: 'center'}">{{ authorText }}</p>
    <div :style="{fontSize: fontSize + 'px'}">

      <!-- Story text -->
      <p v-if="bodyText!=''" :style="{whiteSpace: 'pre-wrap', lineHeight: lineHeight + 'px'}">
        {{ bodyText }}
      </p>

      <!-- Loading state -->
      <div v-else-if="isLoading" class="loading-container">
        <svg xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" style="margin: auto; background: none; display: block; shape-rendering: auto; animation-play-state: running; animation-delay: 0s;" width="84px" height="84px" viewBox="0 0 100 100" preserveAspectRatio="xMidYMid">
          <circle cx="50" cy="50" fill="none" stroke="#91d5ed" stroke-width="10" r="35" stroke-dasharray="164.93361431346415 56.97787143782138" style="animation-play-state: running; animation-delay: 0s;">
            <animateTransform attributeName="transform" type="rotate" repeatCount="indefinite" dur="1s" values="0 50 50;360 50 50" keyTimes="0;1" style="animation-play-state: running; animation-delay: 0s;"></animateTransform>
          </circle>
        </svg>
        <p class="loading-text">{{$t("loading_story")}}</p>
        <div class="skeleton-container">
          <div class="skeleton skeleton-title"></div>
          <div class="skeleton skeleton-author"></div>
          <div class="skeleton skeleton-line"></div>
          <div class="skeleton skeleton-line"></div>
          <div class="skeleton skeleton-line"></div>
          <div class="skeleton skeleton-line short"></div>
        </div>
      </div>

      <!-- Error state -->
      <div v-else-if="errorMessage" class="error-container">
        <i class="fas fa-exclamation-circle error-icon" aria-hidden="true"></i>
        <p class="error-message">{{ errorMessage }}</p>
        <!-- Suppressed when rate limited: each retry writes another access_log
             row and pushes the user further past the threshold. -->
        <button v-if="!isRateLimited" @click="onRetry" class="retry-button" :class="styles">
          <i class="fas fa-redo" aria-hidden="true"></i>
          {{ $t("retry") }}
        </button>
      </div>

      <!-- Welcome / Intro -->
      <div v-else class="welcome-container">
        <p class="welcome-tagline">{{ $t('welcome_tagline') }}</p>

        <div class="steps-grid">
          <div class="step-card" :class="styles">
            <div class="step-icon">
              <i class="fas fa-clock" aria-hidden="true"></i>
            </div>
            <h3>{{ $t('step_1_title') }}</h3>
            <p>{{ $t('step_1_desc') }}</p>
          </div>

          <div class="step-card" :class="styles">
            <div class="step-icon">
              <i class="fas fa-book-open" aria-hidden="true"></i>
            </div>
            <h3>{{ $t('step_2_title') }}</h3>
            <p>{{ $t('step_2_desc') }}</p>
          </div>

          <div class="step-card" :class="styles">
            <div class="step-icon">
              <i class="fas fa-star" aria-hidden="true"></i>
            </div>
            <h3>{{ $t('step_3_title') }}</h3>
            <p>{{ $t('step_3_desc') }}</p>
          </div>
        </div>

        <div class="welcome-tip" :class="styles">
          <i class="fas fa-plane" aria-hidden="true"></i>
          <p>{{ $t('welcome_tip') }}</p>
        </div>
      </div>

      <!-- Story end actions: share + reactions -->
      <div v-if="bodyText!=''" class="story-end-actions">
        <button v-if="shareStory" @click="shareStory" class="share-btn" :class="styles">
          <i class="fas fa-share-nodes" aria-hidden="true"></i>
          {{ $t('share') }}
        </button>

        <div class="reactions-row">
          <button :class="['reaction-button', styles]"
                  :title="$t('like')"
                  :aria-label="$t('like')"
                  @click="sendReaction('like')">
            <i class="fa-solid fa-thumbs-up" aria-hidden="true"></i>
          </button>
          <button :class="['reaction-button', styles]"
                  :title="$t('dislike')"
                  :aria-label="$t('dislike')"
                  @click="sendReaction('dislike')">
            <i class="fa-solid fa-thumbs-down" aria-hidden="true"></i>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'Body',
  props: ["bodyText", "titleText", "authorText", "styles", "fontSize",
          "lineHeight", "isLoading", "sendReaction", "shareStory", "errorMessage", "onRetry",
          "isRateLimited"],
}
</script>

<style scoped>
.story-author {
  margin-top: 4px;
  margin-bottom: 0;
  opacity: 0.85;
  font-weight: 600;
}

.body {
  text-align: left;
  margin-left: auto;
  margin-right: auto;
  padding-left: 8vw;
  padding-right: 8vw;
  padding-top: 40px;
  padding-bottom: 120px;
  max-width: 800px;
}

@media (max-width: 768px) {
  .body {
    padding-left: 5vw;
    padding-right: 5vw;
    padding-top: 24px;
    padding-bottom: 150px;
  }
}

/* ====== Welcome Section ====== */
.welcome-container {
  text-align: center;
  padding-top: 20px;
}

.welcome-tagline {
  font-size: 18px;
  line-height: 1.6;
  opacity: 0.85;
  max-width: 500px;
  margin: 0 auto 40px;
}

.steps-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 20px;
  margin-bottom: 32px;
}

@media (max-width: 600px) {
  .steps-grid {
    grid-template-columns: 1fr;
    gap: 16px;
  }
}

.step-card {
  padding: 24px 16px;
  border-radius: 12px;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}

.step-card.lightMode {
  background: #fff;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
}

.step-card.darkMode {
  background: #252540;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
}

.step-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.1);
}

.step-icon {
  width: 48px;
  height: 48px;
  margin: 0 auto 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  background: linear-gradient(135deg, #3498db, #2ecc71);
  color: white;
  font-size: 20px;
}

.step-card h3 {
  font-size: 16px;
  margin: 0 0 8px;
  font-weight: 700;
}

.step-card p {
  font-size: 14px;
  line-height: 1.5;
  margin: 0;
  opacity: 0.8;
}

.welcome-tip {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px 20px;
  border-radius: 10px;
  font-size: 13px;
  text-align: left;
  max-width: 480px;
  margin: 0 auto;
}

.welcome-tip.lightMode {
  background: #e8f4fd;
  color: #1a5276;
}

.welcome-tip.darkMode {
  background: #1e2d4a;
  color: #a3c9e8;
}

.welcome-tip i {
  font-size: 18px;
  flex-shrink: 0;
}

.welcome-tip p {
  margin: 0;
  line-height: 1.4;
}

/* ====== Story End Actions ====== */
.story-end-actions {
  text-align: center;
  margin-top: 40px;
  padding-top: 24px;
  border-top: 1px solid rgba(128, 128, 128, 0.15);
}

.share-btn {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 10px 24px;
  border: 2px solid;
  border-radius: 8px;
  font-size: 14px;
  font-family: inherit;
  cursor: pointer;
  transition: all 0.2s ease;
  background: none;
  margin-bottom: 24px;
}

.share-btn.lightMode {
  border-color: #c0c8d4;
  color: #00073F;
}

.share-btn.lightMode:hover {
  border-color: #3498db;
  color: #3498db;
}

.share-btn.darkMode {
  border-color: #3a3a55;
  color: #e8e8e8;
}

.share-btn.darkMode:hover {
  border-color: #5dade2;
  color: #5dade2;
}

.reactions-row {
  display: flex;
  justify-content: center;
  gap: 24px;
}

.reaction-button {
  background: none;
  border: none;
  padding: 12px;
  cursor: pointer;
  font-size: 56px;
  transition: color 0.3s ease, transform 0.2s ease;
  outline: none;
}

.reaction-button i {
  pointer-events: none;
}

.reaction-button:hover {
  transform: scale(1.15);
  filter: brightness(1.2);
}

.reaction-button:focus {
  outline: 3px solid #a2a2d0;
  outline-offset: 8px;
  border-radius: 8px;
  transform: scale(1.1);
}

.reaction-button:active {
  transform: scale(0.9);
}

/* ====== Loading ====== */
.loading-container {
  text-align: center;
  padding: 40px 0;
}

.loading-text {
  margin-top: 20px;
  font-size: 18px;
  color: #666;
  animation: pulse 1.5s ease-in-out infinite;
}

@keyframes pulse {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.5; }
}

.skeleton-container {
  margin-top: 40px;
  text-align: left;
}

.skeleton {
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 200% 100%;
  animation: loading 1.5s ease-in-out infinite;
  border-radius: 4px;
  margin-bottom: 12px;
}

@keyframes loading {
  0% { background-position: 200% 0; }
  100% { background-position: -200% 0; }
}

.skeleton-title {
  height: 32px;
  width: 60%;
  margin: 0 auto 16px;
}

.skeleton-author {
  height: 18px;
  width: 40%;
  margin: 0 auto 24px;
}

.skeleton-line {
  height: 16px;
  width: 100%;
}

.skeleton-line.short {
  width: 70%;
}

.darkMode .skeleton {
  background: linear-gradient(90deg, #2a2a40 25%, #3a3a55 50%, #2a2a40 75%);
  background-size: 200% 100%;
}

.darkMode .loading-text {
  color: #ccc;
}

/* ====== Error ====== */
.error-container {
  text-align: center;
  padding: 60px 20px;
}

.error-icon {
  font-size: 64px;
  color: #e74c3c;
  margin-bottom: 20px;
}

.error-message {
  font-size: 18px;
  color: #e74c3c;
  margin-bottom: 30px;
  line-height: 1.6;
}

.retry-button {
  background-color: #3498db;
  color: white;
  border: none;
  padding: 12px 24px;
  font-size: 16px;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.3s ease;
  display: inline-flex;
  align-items: center;
  gap: 8px;
}

.retry-button:hover {
  background-color: #2980b9;
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(52, 152, 219, 0.3);
}

.retry-button:active {
  transform: translateY(0);
}

.retry-button:focus {
  outline: 2px solid #3498db;
  outline-offset: 2px;
}

.retry-button.darkMode {
  background-color: #5dade2;
}

.retry-button.darkMode:hover {
  background-color: #3498db;
}
</style>
